import { FC } from 'react';
import { Controller } from 'react-hook-form';
import { Grid, Stack, TextField, Typography } from '@mui/material';

import { useDictionary } from '@cvt/hooks/useDictionary';
import { TagInput } from '@shared/components/TagInput';
import { EmotionalArcSelector } from '@modules/Stories/components/EmotionalArcSelector';


const MaxLength = {
  THEME: 180,
};

export const OutlineFields: FC = () => {
  const dictionary = useDictionary();

  return (
    <Stack gap={3}>
      <Typography variant="body1" fontSize={30} fontWeight={600}>
        Story Outline and Draft Prep
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Optional context to help develop your story
      </Typography>

      <Grid container gap={2}>
        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Theme</Typography>
          <Controller
            rules={{
              required: dictionary.forms.validations.required,
              maxLength: {
                value: MaxLength.THEME,
                message: dictionary.forms.validations.maxLength(MaxLength.THEME),
              },
            }}
            name="theme"
            render={({ field: { ref, ...field }, fieldState }) => (
              <TextField
                {...field}
                inputRef={ref}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={dictionary.forms.story.themePlaceholder}
                slotProps={{ htmlInput: { maxlength: MaxLength.THEME } }}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Core Message</Typography>
          <Controller
            name="coreMessage"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={1}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={dictionary.forms.story.coreMessagePlaceholder}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Controller
            name="emotionalArc"
            rules={{ required: 'Please select an emotional arc' }}
            render={({ field, fieldState }) => (
              <>
                <EmotionalArcSelector
                  value={field.value}
                  onChange={field.onChange}
                />
                {fieldState.error && (
                  <Typography color="error" variant="caption">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Setting (Post-it Notes)</Typography>
          <Controller
            name="setting"
            render={({ field, fieldState }) => (
              <TagInput
                {...field}
                renderInput={({ onChange, value }) => (
                  <TextField
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    multiline
                    minRows={1}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder={dictionary.forms.story.settingPlaceholder}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Notes</Typography>
          <Controller
            name="notes"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={3}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={dictionary.forms.story.notesPlaceholder}
              />
            )}
          />
        </Grid>
      </Grid>

    </Stack>
  );
};
